import {
  DndContext,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
} from "@dnd-kit/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  GripVertical,
  MoveHorizontal,
  Plus,
  Repeat2,
  Search,
  Undo2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import {
  scheduleDateSchema,
  type ActivitySummary,
  type CalendarDay,
  type CalendarResponse,
  type CalendarSession,
} from "@learn-flow/contracts";

import { AppShell } from "../../components/app-shell.js";
import { FlashcardIndicator } from "../../components/flashcard-indicator.js";
import { PageHeaderCreateAction } from "../../components/page-header-create-action.js";
import { createActivityReturnState } from "../../lib/activity-return-context.js";
import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import { createActivitySession } from "../activities/activity-api.js";
import { createCalendarActivityEditorUrl } from "../activities/calendar-activity-creation-intent.js";
import { libraryQueryKey, libraryQueryRoot, loadLibrary } from "../library/library-api.js";
import {
  calendarQueryKey,
  calendarQueryRoot,
  duplicateCalendarSession,
  loadCalendar,
  moveCalendarSession,
  reorderCalendarDay,
} from "./calendar-api.js";
import {
  addLocalDays,
  formatDayName,
  formatDayNumber,
  formatFullDate,
  formatMinutes,
  formatRangeLabel,
} from "./calendar-date.js";
import { calendarPageSizeWord, useCalendarPageSize } from "./calendar-layout.js";

type MoveIntent = {
  sessionId: string;
  targetDate: string;
};

type MoveCommand = {
  session: CalendarSession;
  targetDate: string;
  idempotencyKey: string;
  purpose?: "move" | "undo";
};

type MoveFailure = {
  message: string;
  retry: MoveCommand | null;
  retryLabel?: string;
};

type MoveConfirmation = {
  sourceSession: CalendarSession;
  movedSession: CalendarSession;
};

type MovePickerState = {
  session: CalendarSession;
  trigger: HTMLButtonElement;
};

type ReorderDirection = "up" | "down";

type ReorderIntent = {
  sessionId: string;
  targetSessionId: string | null;
  focusDirection?: ReorderDirection;
};

type ReorderCommand = {
  scheduledDate: string;
  sessionId: string;
  sessionTitle: string;
  sessionIds: string[];
  version: number;
  idempotencyKey: string;
  focusDirection?: ReorderDirection;
};

type ReorderFailure = {
  message: string;
  retry: ReorderCommand | null;
};

type DuplicateCommand = {
  session: CalendarSession;
  idempotencyKey: string;
};

type DuplicateFailure = {
  message: string;
  retry: DuplicateCommand | null;
};

export function CalendarPage(): React.JSX.Element {
  const [parameters, setParameters] = useSearchParams();
  const location = useLocation();
  const pageSize = useCalendarPageSize();
  const rawStartDate = parameters.get("start");
  const parsedStartDate = scheduleDateSchema.safeParse(rawStartDate);
  const startDate = parsedStartDate.success ? parsedStartDate.data : null;
  const parsedSelectedDate = scheduleDateSchema.safeParse(parameters.get("selected"));
  const selectedDate = parsedSelectedDate.success ? parsedSelectedDate.data : null;
  const queryClient = useQueryClient();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState(() =>
    calendarActivityScheduledAnnouncement(location.state),
  );
  const [suppressHeaderCreate, setSuppressHeaderCreate] = useState(() =>
    Boolean(calendarActivityScheduledAnnouncement(location.state)),
  );
  const [moveFailure, setMoveFailure] = useState<MoveFailure | null>(null);
  const [moveConfirmation, setMoveConfirmation] = useState<MoveConfirmation | null>(null);
  const [undoNotice, setUndoNotice] = useState("");
  const [movePicker, setMovePicker] = useState<MovePickerState | null>(null);
  const [reorderFailure, setReorderFailure] = useState<ReorderFailure | null>(null);
  const [duplicateFailure, setDuplicateFailure] = useState<DuplicateFailure | null>(null);
  const [addDate, setAddDate] = useState<string | null>(null);
  const suppressClick = useRef<string | null>(null);
  const moveInFlight = useRef(false);
  const reorderInFlight = useRef(false);
  const duplicateInFlight = useRef(false);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );
  const calendar = useQuery({
    queryKey: calendarQueryKey(startDate),
    queryFn: () => loadCalendar(startDate),
  });
  const closeMovePicker = useCallback(() => setMovePicker(null), []);

  useEffect(() => {
    document.title = "Calendar · MemoKite";
  }, []);
  useEffect(() => {
    if (!suppressHeaderCreate) return;
    const timer = window.setTimeout(() => setSuppressHeaderCreate(false), 500);
    return () => window.clearTimeout(timer);
  }, [suppressHeaderCreate]);
  useEffect(() => {
    if (!calendar.data || startDate === calendar.data.range.startDate) return;
    const nextParameters = new URLSearchParams(parameters);
    nextParameters.set("start", calendar.data.range.startDate);
    setParameters(nextParameters, { replace: true });
  }, [calendar.data, parameters, setParameters, startDate]);

  const focusSession = useCallback((sessionId: string) => {
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(`[data-session-id="${sessionId}"] [data-event-link]`)
        ?.focus();
    });
  }, []);
  const focusDuplicate = useCallback((sessionId: string) => {
    window.requestAnimationFrame(() => {
      const card = document.querySelector<HTMLElement>(`[data-session-id="${sessionId}"]`);
      card?.querySelector<HTMLButtonElement>("[data-drag-handle]")?.focus();
    });
  }, []);
  const focusReorder = useCallback((sessionId: string, direction?: ReorderDirection) => {
    if (!direction) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const card = document.querySelector<HTMLElement>(`[data-session-id="${sessionId}"]`);
        card?.querySelector<HTMLButtonElement>("[data-drag-handle]")?.focus();
      });
    });
  }, []);

  const move = useMutation({
    mutationFn: ({ session, targetDate, idempotencyKey }: MoveCommand) =>
      moveCalendarSession(session.id, {
        version: session.version,
        scheduledDate: targetDate,
        idempotencyKey,
      }),
    onMutate: async ({ session, targetDate }) => {
      setMoveFailure(null);
      setReorderFailure(null);
      setDuplicateFailure(null);
      setMoveConfirmation(null);
      setUndoNotice("");
      await queryClient.cancelQueries({ queryKey: calendarQueryRoot });
      const key = calendarQueryKey(startDate);
      const previous = queryClient.getQueryData<CalendarResponse>(key);
      if (previous) {
        queryClient.setQueryData(key, relocateSession(previous, session.id, targetDate));
      }
      focusSession(session.id);
      return { previous, key, session, targetDate };
    },
    onSuccess: async (result, command, context) => {
      if (!context) return;
      queryClient.setQueryData<CalendarResponse>(context.key, (current) =>
        current ? replaceSession(current, result.session) : current,
      );
      const day = formatFullDate(result.session.scheduledDate);
      if (command.purpose === "undo") {
        const notice = `${result.session.activityTitle} restored to ${day}.`;
        setAnnouncement(notice);
        setUndoNotice(`Move undone. ${notice}`);
      } else {
        const notice = `${result.session.activityTitle} moved to ${day}.`;
        setAnnouncement(notice);
        setMoveConfirmation({
          sourceSession: context.session,
          movedSession: result.session,
        });
      }
      focusSession(result.session.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: calendarQueryRoot }),
        queryClient.invalidateQueries({
          queryKey: ["activity-detail", result.session.activityId],
        }),
        queryClient.invalidateQueries({ queryKey: ["today"] }),
      ]);
    },
    onError: (error, command, context) => {
      if (!context) return;
      if (context.previous) queryClient.setQueryData(context.key, context.previous);
      const scheduleChanged =
        error instanceof ApiError &&
        (error.problem?.code === "CALENDAR_SESSION_VERSION_CONFLICT" ||
          error.problem?.code === "CALENDAR_SESSION_NOT_FOUND" ||
          error.problem?.code === "CALENDAR_SESSION_HISTORY_IMMUTABLE");
      const undoDestination = formatFullDate(command.session.scheduledDate);
      setMoveFailure({
        message:
          command.purpose === "undo"
            ? scheduleChanged
              ? `The schedule changed in another view. The session remains on ${undoDestination}; refresh the Calendar before trying again.`
              : error instanceof ApiError
                ? `${error.message} The session remains on ${undoDestination}.`
                : `The move could not be undone. The session remains on ${undoDestination}; please try again.`
            : scheduleChanged
              ? "The schedule changed in another view. Refresh the Calendar before moving it again."
              : error instanceof ApiError
                ? `${error.message} Nothing was moved.`
                : "That session could not be moved. Nothing changed.",
        retry: scheduleChanged ? null : command,
        retryLabel: command.purpose === "undo" ? "Try undo again" : "Try move again",
      });
      if (scheduleChanged) void queryClient.invalidateQueries({ queryKey: calendarQueryRoot });
      focusSession(context.session.id);
    },
    onSettled: () => {
      moveInFlight.current = false;
    },
  });

  const submitMove = useCallback(
    (command: MoveCommand): boolean => {
      if (moveInFlight.current || reorderInFlight.current || duplicateInFlight.current)
        return false;
      moveInFlight.current = true;
      move.mutate(command);
      return true;
    },
    [move],
  );

  const requestMove = useCallback(
    (intent: MoveIntent) => {
      const response = queryClient.getQueryData<CalendarResponse>(calendarQueryKey(startDate));
      const session = response ? findSession(response, intent.sessionId) : undefined;
      if (!session || moveInFlight.current || reorderInFlight.current || duplicateInFlight.current)
        return;
      if (session.status !== "scheduled") {
        setMoveFailure({
          message: "Completed sessions are kept as history and cannot be moved.",
          retry: null,
        });
        focusSession(session.id);
        return;
      }
      if (session.scheduledDate === intent.targetDate) return;
      submitMove({
        session,
        targetDate: intent.targetDate,
        idempotencyKey: createUuid(),
      });
    },
    [focusSession, queryClient, startDate, submitMove],
  );

  const undoMove = useCallback(() => {
    if (!moveConfirmation) return;
    submitMove({
      session: moveConfirmation.movedSession,
      targetDate: moveConfirmation.sourceSession.scheduledDate,
      idempotencyKey: createUuid(),
      purpose: "undo",
    });
  }, [moveConfirmation, submitMove]);

  const reorder = useMutation({
    mutationFn: (command: ReorderCommand) =>
      reorderCalendarDay(command.scheduledDate, {
        version: command.version,
        sessionIds: command.sessionIds,
        idempotencyKey: command.idempotencyKey,
      }),
    onMutate: async (command) => {
      setAnnouncement("");
      setMoveFailure(null);
      setMoveConfirmation(null);
      setUndoNotice("");
      setReorderFailure(null);
      setDuplicateFailure(null);
      await queryClient.cancelQueries({ queryKey: calendarQueryRoot });
      const key = calendarQueryKey(startDate);
      const previous = queryClient.getQueryData<CalendarResponse>(key);
      if (previous) {
        queryClient.setQueryData(
          key,
          reorderSessions(previous, command.scheduledDate, command.sessionIds),
        );
      }
      focusReorder(command.sessionId, command.focusDirection);
      return { previous, key };
    },
    onSuccess: async (result, command, context) => {
      if (!context) return;
      queryClient.setQueryData<CalendarResponse>(context.key, (current) =>
        current
          ? setDayOrderVersion(
              reorderSessions(current, result.scheduledDate, result.sessionIds),
              result.scheduledDate,
              result.orderVersion,
            )
          : current,
      );
      setAnnouncement(
        command.focusDirection
          ? `${command.sessionTitle} moved ${command.focusDirection}.`
          : `${command.sessionTitle} reordered on ${formatFullDate(command.scheduledDate)}.`,
      );
      focusReorder(command.sessionId, command.focusDirection);
      await queryClient.invalidateQueries({ queryKey: calendarQueryRoot });
    },
    onError: (error, command, context) => {
      if (context?.previous) queryClient.setQueryData(context.key, context.previous);
      const scheduleChanged =
        error instanceof ApiError &&
        (error.problem?.code === "CALENDAR_DAY_ORDER_CONFLICT" ||
          error.problem?.code === "CALENDAR_DAY_SESSIONS_CHANGED");
      setReorderFailure({
        message: scheduleChanged
          ? "The card order changed in another view. Refresh the Calendar before reordering again."
          : error instanceof ApiError
            ? `${error.message} The previous card order was restored.`
            : "The cards could not be reordered. The previous order was restored.",
        retry: scheduleChanged ? null : command,
      });
      if (scheduleChanged) void queryClient.invalidateQueries({ queryKey: calendarQueryRoot });
      focusReorder(command.sessionId, command.focusDirection);
    },
    onSettled: () => {
      reorderInFlight.current = false;
    },
  });

  const submitReorder = useCallback(
    (command: ReorderCommand) => {
      if (moveInFlight.current || reorderInFlight.current || duplicateInFlight.current) return;
      reorderInFlight.current = true;
      reorder.mutate(command);
    },
    [reorder],
  );

  const requestReorder = useCallback(
    (intent: ReorderIntent) => {
      const response = queryClient.getQueryData<CalendarResponse>(calendarQueryKey(startDate));
      const session = response ? findSession(response, intent.sessionId) : undefined;
      if (!response || !session) return;
      const day = response.days.find((candidate) => candidate.date === session.scheduledDate);
      if (!day || day.sessions.length < 2) return;
      const currentIds = day.sessions.map((candidate) => candidate.id);
      const sourceIndex = currentIds.indexOf(session.id);
      if (sourceIndex < 0) return;
      if (intent.focusDirection) {
        const destinationIndex = sourceIndex + (intent.focusDirection === "up" ? -1 : 1);
        if (destinationIndex < 0 || destinationIndex >= currentIds.length) return;
        const sessionIds = [...currentIds];
        [sessionIds[sourceIndex], sessionIds[destinationIndex]] = [
          sessionIds[destinationIndex]!,
          sessionIds[sourceIndex]!,
        ];
        submitReorder({
          scheduledDate: day.date,
          sessionId: session.id,
          sessionTitle: session.activityTitle,
          sessionIds,
          version: day.orderVersion,
          idempotencyKey: createUuid(),
          focusDirection: intent.focusDirection,
        });
        return;
      }
      const targetIndex = intent.targetSessionId
        ? currentIds.indexOf(intent.targetSessionId)
        : currentIds.length;
      if (targetIndex < 0 || intent.targetSessionId === session.id) return;
      const sessionIds = currentIds.filter((id) => id !== session.id);
      const insertionIndex = intent.targetSessionId
        ? sessionIds.indexOf(intent.targetSessionId) + (sourceIndex < targetIndex ? 1 : 0)
        : sessionIds.length;
      sessionIds.splice(insertionIndex, 0, session.id);
      if (sessionIds.every((id, index) => id === currentIds[index])) return;
      submitReorder({
        scheduledDate: day.date,
        sessionId: session.id,
        sessionTitle: session.activityTitle,
        sessionIds,
        version: day.orderVersion,
        idempotencyKey: createUuid(),
        ...(intent.focusDirection ? { focusDirection: intent.focusDirection } : {}),
      });
    },
    [queryClient, startDate, submitReorder],
  );

  const duplicate = useMutation({
    mutationFn: ({ session, idempotencyKey }: DuplicateCommand) =>
      duplicateCalendarSession(session.id, {
        version: session.version,
        idempotencyKey,
      }),
    onMutate: () => {
      setAnnouncement("");
      setMoveFailure(null);
      setMoveConfirmation(null);
      setUndoNotice("");
      setDuplicateFailure(null);
    },
    onSuccess: async (result) => {
      const key = calendarQueryKey(startDate);
      queryClient.setQueryData<CalendarResponse>(key, (current) =>
        current ? insertSession(current, result.session) : current,
      );
      setAnnouncement(
        `${result.session.activityTitle} duplicated. The new session is ready to move.`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: calendarQueryRoot }),
        queryClient.invalidateQueries({
          queryKey: ["activity-detail", result.session.activityId],
        }),
        queryClient.invalidateQueries({ queryKey: ["today"] }),
      ]);
      focusDuplicate(result.session.id);
    },
    onError: (error, command) => {
      const scheduleChanged =
        error instanceof ApiError &&
        (error.problem?.code === "CALENDAR_SESSION_VERSION_CONFLICT" ||
          error.problem?.code === "CALENDAR_SESSION_NOT_FOUND" ||
          error.problem?.code === "CALENDAR_SESSION_HISTORY_IMMUTABLE");
      setDuplicateFailure({
        message: scheduleChanged
          ? "The schedule changed in another view. Refresh the Calendar before duplicating it."
          : error instanceof ApiError
            ? `${error.message} No additional session was shown.`
            : "That session could not be duplicated. No additional session was shown.",
        retry: scheduleChanged ? null : command,
      });
      if (scheduleChanged) void queryClient.invalidateQueries({ queryKey: calendarQueryRoot });
      focusSession(command.session.id);
    },
    onSettled: () => {
      duplicateInFlight.current = false;
    },
  });

  const submitDuplicate = useCallback(
    (command: DuplicateCommand) => {
      if (duplicateInFlight.current || moveInFlight.current || reorderInFlight.current) return;
      duplicateInFlight.current = true;
      duplicate.mutate(command);
    },
    [duplicate],
  );

  const requestDuplicate = useCallback(
    (sessionId: string) => {
      const response = queryClient.getQueryData<CalendarResponse>(calendarQueryKey(startDate));
      const session = response ? findSession(response, sessionId) : undefined;
      if (!session || session.status !== "scheduled") return;
      submitDuplicate({ session, idempotencyKey: createUuid() });
    },
    [queryClient, startDate, submitDuplicate],
  );

  const handleDragStart = (event: DragStartEvent) => {
    setAnnouncement("");
    setMoveFailure(null);
    setActiveSessionId(String(event.active.id));
  };
  const handleDragEnd = (event: DragEndEvent) => {
    const sessionId = String(event.active.id);
    setActiveSessionId(null);
    suppressClick.current = sessionId;
    window.setTimeout(() => {
      if (suppressClick.current === sessionId) suppressClick.current = null;
    }, 350);
    if (!event.over) return;
    const drop = event.over.data.current as
      | { kind: "day"; scheduledDate: string }
      | { kind: "session"; scheduledDate: string; sessionId: string }
      | { kind: "edge"; scheduledDate: string }
      | undefined;
    if (!drop) return;
    const targetDate = drop.scheduledDate;
    const visible = calendar.data?.days.slice(0, pageSize).some((day) => day.date === targetDate);
    if (!visible && drop.kind !== "edge") return;
    const session = calendar.data ? findSession(calendar.data, sessionId) : undefined;
    if (session?.scheduledDate === targetDate) {
      requestReorder({
        sessionId,
        targetSessionId: drop.kind === "session" ? drop.sessionId : null,
      });
      return;
    }
    requestMove({ sessionId, targetDate });
  };

  if (calendar.isPending) {
    return <CalendarState title="Preparing your week" busy />;
  }
  if (calendar.isError) {
    return (
      <CalendarState
        title="Your Calendar could not be loaded"
        action={
          <button type="button" className="soft-button" onClick={() => void calendar.refetch()}>
            Try again
          </button>
        }
      />
    );
  }

  const response = calendar.data;
  const highlightedDate = selectedDate ?? response.range.currentDate;
  const visibleDays = response.days.slice(0, pageSize);
  const firstDay = visibleDays[0]!;
  const lastDay = visibleDays.at(-1)!;
  const pageSizeWord = calendarPageSizeWord(pageSize);
  const activityReturnState = createActivityReturnState(`${location.pathname}${location.search}`);
  const activeSession = activeSessionId ? findSession(response, activeSessionId) : undefined;
  const showBoundaryTargets = activeSession?.status === "scheduled";
  const previousOffscreenDate = addLocalDays(firstDay.date, -1);
  const nextOffscreenDate = addLocalDays(lastDay.date, 1);
  return (
    <AppShell mainClassName="calendar-screen">
      <header className="topbar calendar-topbar">
        <div>
          <h1>Calendar</h1>
        </div>
        <PageHeaderCreateAction label="Plan activity" temporarilyDisabled={suppressHeaderCreate} />
      </header>

      {moveFailure ? (
        <div className="calendar-error" role="alert">
          <span>{moveFailure.message}</span>
          {moveFailure.retry ? (
            <button
              type="button"
              className="soft-button"
              disabled={move.isPending}
              onClick={() => {
                if (moveFailure.retry) submitMove(moveFailure.retry);
              }}
            >
              {moveFailure.retryLabel ?? "Try move again"}
            </button>
          ) : (
            <button type="button" className="soft-button" onClick={() => void calendar.refetch()}>
              Refresh Calendar
            </button>
          )}
        </div>
      ) : null}
      {reorderFailure ? (
        <div className="calendar-error" role="alert">
          <span>{reorderFailure.message}</span>
          {reorderFailure.retry ? (
            <button
              type="button"
              className="soft-button"
              disabled={reorder.isPending}
              onClick={() => {
                if (reorderFailure.retry) submitReorder(reorderFailure.retry);
              }}
            >
              Try reorder again
            </button>
          ) : (
            <button type="button" className="soft-button" onClick={() => void calendar.refetch()}>
              Refresh Calendar
            </button>
          )}
        </div>
      ) : null}
      {duplicateFailure ? (
        <div className="calendar-error" role="alert">
          <span>{duplicateFailure.message}</span>
          {duplicateFailure.retry ? (
            <button
              type="button"
              className="soft-button"
              disabled={duplicate.isPending}
              onClick={() => {
                if (duplicateFailure.retry) submitDuplicate(duplicateFailure.retry);
              }}
            >
              Try duplicate again
            </button>
          ) : (
            <button type="button" className="soft-button" onClick={() => void calendar.refetch()}>
              Refresh Calendar
            </button>
          )}
        </div>
      ) : null}
      {moveConfirmation ? (
        <div
          className="calendar-move-confirmation"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span>
            {moveConfirmation.movedSession.activityTitle} moved to{" "}
            <strong>{formatFullDate(moveConfirmation.movedSession.scheduledDate)}</strong>.
          </span>
          <div className="calendar-move-confirmation-actions">
            <button
              type="button"
              className="soft-button"
              onClick={() => {
                const nextParameters = new URLSearchParams(parameters);
                nextParameters.set("start", moveConfirmation.movedSession.scheduledDate);
                nextParameters.set("selected", moveConfirmation.movedSession.scheduledDate);
                setParameters(nextParameters, { replace: false });
              }}
            >
              View day
            </button>
            <button
              type="button"
              className="soft-button"
              disabled={move.isPending}
              onClick={undoMove}
            >
              <Undo2 size={14} aria-hidden="true" />
              Undo
            </button>
          </div>
        </div>
      ) : null}
      {undoNotice ? (
        <div
          className="calendar-move-confirmation is-undo"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span>{undoNotice}</span>
          <button type="button" className="soft-button" onClick={() => setUndoNotice("")}>
            Dismiss
          </button>
        </div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={calendarCollisionDetection}
        onDragStart={handleDragStart}
        onDragCancel={() => setActiveSessionId(null)}
        onDragEnd={handleDragEnd}
      >
        <section className="calendar-shell" aria-labelledby="calendar-range-heading">
          <div className="calendar-main">
            <div className="calendar-header">
              <div className="calendar-range-navigation">
                <button
                  type="button"
                  className="icon-button bordered"
                  aria-label={`Previous ${pageSizeWord} days`}
                  onClick={() => {
                    const nextParameters = new URLSearchParams(parameters);
                    nextParameters.set("start", addLocalDays(firstDay.date, -pageSize));
                    setParameters(nextParameters, { replace: false });
                  }}
                >
                  <ChevronLeft size={18} />
                </button>
                <h2 id="calendar-range-heading">{formatRangeLabel(firstDay.date, lastDay.date)}</h2>
                <button
                  type="button"
                  className="icon-button bordered"
                  aria-label={`Next ${pageSizeWord} days`}
                  onClick={() => {
                    const nextParameters = new URLSearchParams(parameters);
                    nextParameters.set("start", addLocalDays(firstDay.date, pageSize));
                    setParameters(nextParameters, { replace: false });
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
              <button
                type="button"
                className="soft-button calendar-today-button"
                onClick={() => {
                  const nextParameters = new URLSearchParams();
                  nextParameters.set("start", response.range.currentDate);
                  setParameters(nextParameters, { replace: false });
                }}
              >
                <CalendarDays size={16} aria-hidden="true" />
                Move to today
              </button>
            </div>
            <span
              className="sr-only"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              aria-hidden={moveConfirmation || undoNotice ? "true" : undefined}
            >
              {announcement}
            </span>
            <div
              className="calendar-week-scroll"
              role="region"
              aria-label={`${pageSizeWord[0]!.toUpperCase()}${pageSizeWord.slice(1)}-day Calendar`}
            >
              {showBoundaryTargets ? (
                <>
                  <CalendarEdgeDropZone
                    direction="previous"
                    scheduledDate={previousOffscreenDate}
                  />
                  <CalendarEdgeDropZone direction="next" scheduledDate={nextOffscreenDate} />
                </>
              ) : null}
              <div
                className="week-grid"
                style={{
                  gridTemplateColumns: `repeat(${pageSize.toString()}, minmax(0, 1fr))`,
                }}
              >
                {visibleDays.map((day) => (
                  <CalendarDayColumn
                    key={day.date}
                    day={day}
                    isToday={day.date === response.range.currentDate}
                    isHighlighted={day.date === highlightedDate}
                    savingSessionId={
                      reorder.isPending
                        ? reorder.variables.sessionId
                        : move.isPending
                          ? move.variables.session.id
                          : duplicate.isPending
                            ? duplicate.variables.session.id
                            : null
                    }
                    activeSessionId={activeSessionId}
                    suppressClick={suppressClick}
                    activityReturnState={activityReturnState}
                    onAdd={() => setAddDate(day.date)}
                    onDuplicate={requestDuplicate}
                    onMove={requestMove}
                    onOpenMovePicker={(session, trigger) => setMovePicker({ session, trigger })}
                    onReorder={requestReorder}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </DndContext>

      {addDate ? (
        <AddFromLibraryDialog
          date={addDate}
          returnTo={`${location.pathname}${location.search}`}
          onClose={() => setAddDate(null)}
          onAdded={(title) => {
            setAnnouncement(`${title} added to ${formatFullDate(addDate)}.`);
            setAddDate(null);
          }}
        />
      ) : null}
      {movePicker ? (
        <MoveSessionDialog
          session={movePicker.session}
          returnFocus={movePicker.trigger}
          onClose={closeMovePicker}
          onMove={(targetDate) => requestMove({ sessionId: movePicker.session.id, targetDate })}
        />
      ) : null}
    </AppShell>
  );
}

function CalendarEdgeDropZone({
  direction,
  scheduledDate,
}: {
  direction: "previous" | "next";
  scheduledDate: string;
}): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id: `edge:${direction}:${scheduledDate}`,
    data: { kind: "edge", scheduledDate },
  });
  const directionLabel = direction === "previous" ? "Previous day" : "Next day";
  return (
    <div
      ref={setNodeRef}
      className={`calendar-edge-drop-zone is-${direction} ${isOver ? "is-over" : ""}`}
      role="region"
      aria-label={`${directionLabel}: ${formatFullDate(scheduledDate)}`}
      data-calendar-edge={direction}
      data-calendar-edge-date={scheduledDate}
    >
      <div className="calendar-edge-drop-label">
        <strong>{directionLabel}</strong>
        <span>{formatFullDate(scheduledDate)}</span>
        <small>{isOver ? "Release to move" : "Drop here"}</small>
      </div>
    </div>
  );
}

function CalendarDayColumn({
  day,
  isToday,
  isHighlighted,
  savingSessionId,
  activeSessionId,
  suppressClick,
  activityReturnState,
  onAdd,
  onDuplicate,
  onMove,
  onOpenMovePicker,
  onReorder,
}: {
  day: CalendarDay;
  isToday: boolean;
  isHighlighted: boolean;
  savingSessionId: string | null;
  activeSessionId: string | null;
  suppressClick: React.MutableRefObject<string | null>;
  activityReturnState: ReturnType<typeof createActivityReturnState>;
  onAdd: () => void;
  onDuplicate: (sessionId: string) => void;
  onMove: (intent: MoveIntent) => void;
  onOpenMovePicker: (session: CalendarSession, trigger: HTMLButtonElement) => void;
  onReorder: (intent: ReorderIntent) => void;
}): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id: `day:${day.date}`,
    data: { kind: "day", scheduledDate: day.date },
  });
  return (
    <section
      className={`day-column ${isToday ? "is-today" : ""} ${isHighlighted ? "is-selected" : ""} ${isOver ? "is-drop-target" : ""}`}
      data-calendar-selected={isHighlighted ? "true" : undefined}
      aria-label={`${formatFullDate(day.date)}, ${formatMinutes(
        day.totalActivityDurationMinutes,
      )} of activity time`}
    >
      <div className="day-heading">
        <span>{formatDayName(day.date)}</span>
        <strong aria-current={isHighlighted ? "date" : undefined}>
          {formatDayNumber(day.date)}
        </strong>
        {isToday ? <i>Today</i> : null}
      </div>
      <div ref={setNodeRef} className="day-body" data-calendar-day={day.date}>
        {day.sessions.map((session) => (
          <CalendarEventCard
            key={session.id}
            session={session}
            saving={savingSessionId === session.id}
            dragging={activeSessionId === session.id}
            suppressClick={suppressClick}
            activityReturnState={activityReturnState}
            onDuplicate={onDuplicate}
            onMove={onMove}
            onOpenMovePicker={onOpenMovePicker}
            onReorder={onReorder}
          />
        ))}
        {day.sessions.length === 0 ? <span className="empty-drop-hint">Drop here</span> : null}
        {day.sessions.length > 1 ? <CalendarDayEndTarget scheduledDate={day.date} /> : null}
        <button
          type="button"
          className="add-calendar-item"
          onClick={onAdd}
          aria-label={`Add activity on ${formatFullDate(day.date)}`}
        >
          <Plus size={15} />
        </button>
      </div>
      <div className="day-load">
        {formatMinutes(day.totalActivityDurationMinutes)} activity time
      </div>
    </section>
  );
}

function CalendarDayEndTarget({ scheduledDate }: { scheduledDate: string }): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-end:${scheduledDate}`,
    data: { kind: "day", scheduledDate },
  });
  return (
    <span
      ref={setNodeRef}
      className={`calendar-day-end-target ${isOver ? "is-over" : ""}`}
      data-calendar-day-end
      aria-hidden="true"
    />
  );
}

function CalendarEventCard({
  session,
  saving,
  dragging,
  suppressClick,
  activityReturnState,
  onDuplicate,
  onMove,
  onOpenMovePicker,
  onReorder,
}: {
  session: CalendarSession;
  saving: boolean;
  dragging: boolean;
  suppressClick: React.MutableRefObject<string | null>;
  activityReturnState: ReturnType<typeof createActivityReturnState>;
  onDuplicate: (sessionId: string) => void;
  onMove: (intent: MoveIntent) => void;
  onOpenMovePicker: (session: CalendarSession, trigger: HTMLButtonElement) => void;
  onReorder: (intent: ReorderIntent) => void;
}): React.JSX.Element {
  const movable = session.status === "scheduled";
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef: setDraggableNodeRef,
    transform,
  } = useDraggable({
    id: session.id,
    disabled: saving,
    data: { sessionId: session.id, scheduledDate: session.scheduledDate },
  });
  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
    id: `session:${session.id}`,
    data: { kind: "session", scheduledDate: session.scheduledDate, sessionId: session.id },
    disabled: saving,
  });
  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      setDraggableNodeRef(node);
      setDroppableNodeRef(node);
    },
    [setDraggableNodeRef, setDroppableNodeRef],
  );
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  const moveBy = (days: number) => {
    onMove({ sessionId: session.id, targetDate: addLocalDays(session.scheduledDate, days) });
  };
  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`calendar-event ${
        session.status === "completed" ? "is-completed" : ""
      } ${dragging ? "is-dragging" : ""} ${isOver && !dragging ? "is-reorder-target" : ""}`}
      data-session-id={session.id}
      data-session-status={session.status}
    >
      <div className="event-meta">
        <button
          {...listeners}
          {...attributes}
          ref={setActivatorNodeRef}
          type="button"
          className="event-drag-handle"
          data-drag-handle
          disabled={saving}
          aria-label={
            movable
              ? `Drag ${session.activityTitle} to reorder it or move it to another day`
              : `Drag ${session.activityTitle} to reorder it within ${formatFullDate(session.scheduledDate)}`
          }
          aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
          onKeyDown={(event) => {
            if (!event.altKey) return;
            if (event.key === "ArrowUp" || event.key === "ArrowDown") {
              event.preventDefault();
              onReorder({
                sessionId: session.id,
                targetSessionId: null,
                focusDirection: event.key === "ArrowUp" ? "up" : "down",
              });
            }
          }}
        >
          <GripVertical size={12} aria-hidden="true" />
        </button>
        <span>{session.scheduledTime ?? "Any time"}</span>
        {session.status === "scheduled" ? (
          <button
            type="button"
            className="event-duplicate-button"
            disabled={saving}
            aria-label={`Duplicate ${session.activityTitle}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onDuplicate(session.id)}
          >
            <Copy size={12} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <Link
        data-event-link
        draggable={false}
        to={`/activities/${session.activityId}?sessionId=${session.id}`}
        state={activityReturnState}
        onClick={(event) => {
          if (suppressClick.current === session.id) {
            event.preventDefault();
            suppressClick.current = null;
          }
        }}
        onKeyDown={(event) => {
          if (!event.altKey || !movable) return;
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            moveBy(-1);
          }
          if (event.key === "ArrowRight") {
            event.preventDefault();
            moveBy(1);
          }
        }}
      >
        <strong>{session.activityTitle}</strong>
      </Link>
      <div className="event-signals">
        <span className="event-status">
          {session.status === "completed" ? <CheckCircle2 size={13} aria-hidden="true" /> : null}
          {session.status === "completed" ? "Completed" : "Not completed"}
        </span>
        {session.completedSessionCount > 0 ? (
          <span className="event-session-count">
            <Repeat2 size={13} aria-hidden="true" />
            {formatCompletedSessionCount(session.completedSessionCount)}
          </span>
        ) : null}
      </div>
      <div className="event-footer">
        <span className="event-duration">
          {session.hasFlashcards ? <FlashcardIndicator /> : null}
          <span>{formatMinutes(session.activityDurationMinutes)}</span>
        </span>
        {movable ? (
          <button
            type="button"
            className="event-move-button"
            disabled={saving}
            aria-label={`Move ${session.activityTitle}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => onOpenMovePicker(session, event.currentTarget)}
          >
            <MoveHorizontal size={12} aria-hidden="true" />
            Move…
          </button>
        ) : null}
      </div>
      {saving ? <span className="event-saving">Saving…</span> : null}
    </article>
  );
}

function MoveSessionDialog({
  session,
  returnFocus,
  onClose,
  onMove,
}: {
  session: CalendarSession;
  returnFocus: HTMLButtonElement;
  onClose: () => void;
  onMove: (targetDate: string) => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const restoreFocusTimer = useRef<number | null>(null);
  const [date, setDate] = useState(session.scheduledDate);
  const [dateError, setDateError] = useState("");
  const headingId = `calendar-move-heading-${session.id}`;
  const dateId = `calendar-move-date-${session.id}`;

  useEffect(() => {
    if (restoreFocusTimer.current !== null) {
      window.clearTimeout(restoreFocusTimer.current);
      restoreFocusTimer.current = null;
    }
    dateRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      restoreFocusTimer.current = window.setTimeout(() => returnFocus.focus(), 0);
    };
  }, [onClose, returnFocus]);

  const submitDate = (targetDate: string) => {
    const parsed = scheduleDateSchema.safeParse(targetDate);
    if (!parsed.success) {
      setDateError("Choose a valid date.");
      dateRef.current?.focus();
      return;
    }
    onMove(parsed.data);
    onClose();
  };

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        className="calendar-move-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <div className="dialog-heading">
          <div>
            <span className="section-kicker">Move session</span>
            <h2 id={headingId}>Move {session.activityTitle}</h2>
            <p>Currently on {formatFullDate(session.scheduledDate)}.</p>
          </div>
          <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="calendar-move-quick-actions">
          <button
            type="button"
            className="soft-button"
            onClick={() => submitDate(addLocalDays(session.scheduledDate, -1))}
          >
            <ChevronLeft size={15} aria-hidden="true" />
            Previous day
          </button>
          <button
            type="button"
            className="soft-button"
            onClick={() => submitDate(addLocalDays(session.scheduledDate, 1))}
          >
            Next day
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitDate(date);
          }}
        >
          <label htmlFor={dateId}>Choose another date</label>
          <input
            ref={dateRef}
            id={dateId}
            type="date"
            required
            value={date}
            aria-describedby={dateError ? `${dateId}-error` : undefined}
            onChange={(event) => {
              setDate(event.target.value);
              setDateError("");
            }}
          />
          {dateError ? (
            <span id={`${dateId}-error`} className="field-error" role="alert">
              {dateError}
            </span>
          ) : null}
          <div className="dialog-actions">
            <button type="submit" className="primary-button">
              Move to selected date
            </button>
            <button type="button" className="soft-button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddFromLibraryDialog({
  date,
  returnTo,
  onClose,
  onAdded,
}: {
  date: string;
  returnTo: string;
  onClose: () => void;
  onAdded: (title: string) => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const returnFocus = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  const restoreFocusTimer = useRef<number | null>(null);
  const idempotencyKey = useRef(createUuid());
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const libraryQuery = { search: "", view: "active" } as const;
  const library = useQuery({
    queryKey: libraryQueryKey(libraryQuery),
    queryFn: () => loadLibrary(libraryQuery),
  });
  const activities = useMemo(
    () =>
      (library.data?.activities ?? []).filter((activity) =>
        activity.title.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
      ),
    [library.data?.activities, search],
  );
  const selected = library.data?.activities.find((activity) => activity.id === selectedId);
  const create = useMutation({
    mutationFn: (duplicate: boolean) => {
      if (!selected) throw new Error("Choose an activity.");
      return createActivitySession(selected.id, {
        idempotencyKey: idempotencyKey.current,
        scheduledDate: date,
        scheduledTime: null,
        allowDuplicate: duplicate,
      });
    },
    onSuccess: async () => {
      if (!selected) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: calendarQueryRoot }),
        queryClient.invalidateQueries({ queryKey: ["activity-detail", selected.id] }),
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: ["today"] }),
      ]);
      onAdded(selected.title);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.problem?.code === "EXACT_SESSION_DUPLICATE") {
        setAllowDuplicate(true);
      }
    },
  });

  useEffect(() => {
    const focusTarget = returnFocus.current;
    if (restoreFocusTimer.current !== null) {
      window.clearTimeout(restoreFocusTimer.current);
      restoreFocusTimer.current = null;
    }
    searchRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      restoreFocusTimer.current = window.setTimeout(() => focusTarget?.focus(), 0);
    };
  }, [onClose]);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        className="calendar-add-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-add-heading"
      >
        <div className="dialog-heading">
          <div>
            <span className="section-kicker">Add from Library</span>
            <h2 id="calendar-add-heading">{formatFullDate(date)}</h2>
          </div>
          <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="calendar-activity-tools">
          <label className="search-box calendar-activity-search">
            <Search size={16} />
            <input
              ref={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Find an activity"
              aria-label="Find an activity"
            />
          </label>
          <Link
            className="soft-button calendar-create-activity"
            to={createCalendarActivityEditorUrl({ scheduledDate: date, returnTo })}
          >
            <Plus size={16} aria-hidden="true" />
            Create new activity
          </Link>
        </div>
        {library.isPending ? (
          <p className="dialog-empty" aria-busy="true">
            Loading your Library…
          </p>
        ) : library.isError ? (
          <div className="dialog-empty" role="alert">
            <p>Your Library could not be loaded.</p>
            <button type="button" className="soft-button" onClick={() => void library.refetch()}>
              Try again
            </button>
          </div>
        ) : activities.length > 0 ? (
          <div className="calendar-activity-list" role="radiogroup" aria-label="Library activities">
            {activities.map((activity) => (
              <ActivityChoice
                key={activity.id}
                activity={activity}
                selected={activity.id === selectedId}
                onSelect={() => {
                  setSelectedId(activity.id);
                  setAllowDuplicate(false);
                  create.reset();
                }}
              />
            ))}
          </div>
        ) : (
          <p className="dialog-empty">
            {search ? "No activities match that search." : "Your Library has no active activities."}
          </p>
        )}
        {allowDuplicate ? (
          <div className="schedule-message warning" role="alert">
            <strong>An identical session is already on this day.</strong>
            <span>You can choose another activity or intentionally add another copy.</span>
          </div>
        ) : create.isError ? (
          <div className="schedule-message error" role="alert">
            The activity was not added. Nothing changed. Please try again.
          </div>
        ) : null}
        <div className="dialog-actions">
          <button
            type="button"
            className="primary-button"
            disabled={!selected || create.isPending}
            onClick={() => create.mutate(allowDuplicate)}
          >
            <CalendarDays size={16} />
            {create.isPending ? "Adding…" : allowDuplicate ? "Add another anyway" : "Add to day"}
          </button>
          <button type="button" className="soft-button" onClick={onClose}>
            Keep day open
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityChoice({
  activity,
  selected,
  onSelect,
}: {
  activity: ActivitySummary;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={selected ? "selected" : ""}
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
    >
      <span>
        <strong>{activity.title}</strong>
        <small>
          {activity.format} · {formatMinutes(activity.estimatedMinutes)}
        </small>
      </span>
      <span aria-hidden="true">{selected ? "Selected" : "Choose"}</span>
    </button>
  );
}

function CalendarState({
  title,
  busy = false,
  action,
}: {
  title: string;
  busy?: boolean;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <AppShell mainClassName="calendar-screen">
      <section className="calendar-state" aria-busy={busy} aria-labelledby="calendar-state-heading">
        <CalendarDays size={31} />
        <h1 id="calendar-state-heading">{title}</h1>
        {action}
      </section>
    </AppShell>
  );
}

function findSession(response: CalendarResponse, sessionId: string): CalendarSession | undefined {
  return response.days.flatMap((day) => day.sessions).find((session) => session.id === sessionId);
}

function relocateSession(
  response: CalendarResponse,
  sessionId: string,
  targetDate: string,
): CalendarResponse {
  const session = findSession(response, sessionId);
  if (!session || session.scheduledDate === targetDate) return response;
  return replaceSession(response, { ...session, scheduledDate: targetDate });
}

function replaceSession(
  response: CalendarResponse,
  replacement: CalendarSession,
): CalendarResponse {
  const previous = findSession(response, replacement.id);
  return {
    ...response,
    days: response.days.map((day) => {
      let daySessions = day.sessions.filter((session) => session.id !== replacement.id);
      if (day.date === replacement.scheduledDate) {
        if (previous?.scheduledDate === replacement.scheduledDate) {
          daySessions = day.sessions.map((session) =>
            session.id === replacement.id ? replacement : session,
          );
        } else {
          daySessions = [...daySessions, replacement];
        }
      }
      return {
        ...day,
        sessions: daySessions,
        totalActivityDurationMinutes: daySessions.reduce(
          (total, session) => total + session.activityDurationMinutes,
          0,
        ),
      };
    }),
  };
}

function insertSession(response: CalendarResponse, session: CalendarSession): CalendarResponse {
  if (findSession(response, session.id)) return replaceSession(response, session);
  return {
    ...response,
    metrics: {
      sessionCount: response.metrics.sessionCount + 1,
      completedSessionCount: response.metrics.completedSessionCount,
      totalActivityDurationMinutes:
        response.metrics.totalActivityDurationMinutes + session.activityDurationMinutes,
    },
    days: response.days.map((day) => {
      const daySessions =
        day.date === session.scheduledDate ? [...day.sessions, session] : day.sessions;
      return {
        ...day,
        sessions: daySessions,
        totalActivityDurationMinutes: daySessions.reduce(
          (total, candidate) => total + candidate.activityDurationMinutes,
          0,
        ),
      };
    }),
  };
}

function reorderSessions(
  response: CalendarResponse,
  scheduledDate: string,
  sessionIds: string[],
): CalendarResponse {
  const positions = new Map(sessionIds.map((sessionId, index) => [sessionId, index]));
  return {
    ...response,
    days: response.days.map((day) =>
      day.date === scheduledDate
        ? {
            ...day,
            sessions: [...day.sessions].sort(
              (left, right) =>
                (positions.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
                (positions.get(right.id) ?? Number.MAX_SAFE_INTEGER),
            ),
          }
        : day,
    ),
  };
}

function setDayOrderVersion(
  response: CalendarResponse,
  scheduledDate: string,
  orderVersion: number,
): CalendarResponse {
  return {
    ...response,
    days: response.days.map((day) => (day.date === scheduledDate ? { ...day, orderVersion } : day)),
  };
}

const calendarCollisionDetection: CollisionDetection = (arguments_) => {
  const collisions = pointerWithin(arguments_);
  const edgeCollision = collisions.find((collision) => String(collision.id).startsWith("edge:"));
  if (edgeCollision) return [edgeCollision];
  const endCollision = collisions.find((collision) => String(collision.id).startsWith("day-end:"));
  if (endCollision) return [endCollision];
  const sessionCollisions = collisions.filter(
    (collision) =>
      String(collision.id).startsWith("session:") &&
      collision.id !== `session:${String(arguments_.active.id)}`,
  );
  const pointer = arguments_.pointerCoordinates;
  const sessionCollision = pointer
    ? sessionCollisions.toSorted((left, right) => {
        const leftRect = arguments_.droppableRects.get(left.id);
        const rightRect = arguments_.droppableRects.get(right.id);
        const distance = (rect: typeof leftRect) =>
          rect
            ? Math.hypot(
                pointer.x - (rect.left + rect.width / 2),
                pointer.y - (rect.top + rect.height / 2),
              )
            : Number.MAX_SAFE_INTEGER;
        return distance(leftRect) - distance(rightRect);
      })[0]
    : sessionCollisions[0];
  if (sessionCollision) return [sessionCollision];
  const activeSessionCollision = collisions.find(
    (collision) => collision.id === `session:${String(arguments_.active.id)}`,
  );
  if (activeSessionCollision) return [activeSessionCollision];
  const intersectingSession = rectIntersection(arguments_).find(
    (collision) =>
      String(collision.id).startsWith("session:") &&
      collision.id !== `session:${String(arguments_.active.id)}`,
  );
  if (intersectingSession) return [intersectingSession];
  return collisions;
};

function calendarActivityScheduledAnnouncement(value: unknown): string {
  if (typeof value !== "object" || value === null || !("calendarActivityScheduled" in value)) {
    return "";
  }
  const scheduled = value.calendarActivityScheduled;
  if (typeof scheduled !== "object" || scheduled === null) return "";
  const title = "title" in scheduled && typeof scheduled.title === "string" ? scheduled.title : "";
  const date = "date" in scheduled ? scheduleDateSchema.safeParse(scheduled.date) : null;
  if (!title.trim() || !date?.success) return "";
  return `${title.trim()} created and added to ${formatFullDate(date.data)}.`;
}

function formatCompletedSessionCount(count: number): string {
  return `Reviewed ${count.toString()} ${count === 1 ? "time" : "times"}`;
}
